import React, { ReactNode, useCallback, useEffect, useId, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import i18next from 'i18next'
import i18n from 'i18next'
import has from 'lodash/has'
import last from 'lodash/last'
import { FormInput } from '@microservices/wiskey-react-components'
import { FormHelperText, Typography } from '@mui/material'

import { CommandPickerController } from '@components/hookFormControllers/CommandPickerController'
import { PickerFieldArrayController } from '@components/hookFormControllers/PickerFieldArrayController'
import { IconSettings } from '@components/IconSettings'
import { ScriptValueEditor } from '@components/ScriptValueDialog'
import { ColorSettings } from '@components/TextSettings/components/ColorSettings'

import { ErrorValidJSType } from '@helpers'
import { FIELD_VALUE_TYPE, FORMATS_DATE_OPTIONS, GENERATOR_INPUT_TYPE } from '@constants'
import { ConstFormat } from '@formats'

import { ModalRefPathPicker } from '@gantt/components/GanttCreateOrEdit/components/ModalRefPathPicker' //todo не должно находится тут
import {
  AutocompleteOption,
  ConfigField,
  GANTT_BIND_TYPE_LIST,
} from '@gantt/components/GanttCreateOrEdit/types'
import {
  AutocompleteOptionsObject,
  getCommandsFormattedRequest,
  getKeyByBindType,
} from '@gantt/helpers'
import { ExtOptionFilter, OptionsFilter } from '@gantt/types'

import 'prismjs/components/prism-json'

export type FieldPickerProps = {
  hasCommands?: boolean
  existingPaths?: string[]
  selectedPaths?: string[]
}

type ScriptEditorProps = {
  noValidate?: boolean
  validator?: (value: string) => ErrorValidJSType | undefined
  onAfterValidation?: (state: boolean) => void
}

type CommandPickerProps = {
  isTypeHidden?: boolean
  isMulti?: boolean
  canChangeRefObject?: boolean
  availableRefObjectCodes?: string[]
}

type FormInputProps = {
  selectedOptions?: string[]
}

type NumberInputProps = {
  max?: number
}

type ToggleButtonOption = {
  value: string | boolean
  icon: ReactNode
}

type ToggleButtonProps = {
  toggleButtonOptions?: ToggleButtonOption[]
}

type SpecialProps = {
  fieldPicker?: FieldPickerProps
  scriptEditor?: ScriptEditorProps
  commandPicker?: CommandPickerProps
  formInput?: FormInputProps
  numberInput?: NumberInputProps
  toggleButton?: ToggleButtonProps
}

export type InputProps = {
  language?: string //todo why
  valueInputLabel?: string
  prefix?: string
  object?: AutocompleteOption<string> | null
  inputType?: InputType
  optionsFilter?: OptionsFilter
  isDisabled?: boolean
  onSaveField?: (value: ConfigField) => void
  clearColor?: string
} & SpecialProps

type Props = {
  getPath: (key: GANTT_BIND_TYPE_LIST | string) => string
  type: GANTT_BIND_TYPE_LIST
  hint?: string
  placeholder?: string
  options?: ExtOptionFilter[]
  children?: ReactNode
  errorMessage?: string
  isShowFormatDateField?: boolean
} & InputProps

export type InputType = GENERATOR_INPUT_TYPE | INPUT_TYPE

export enum INPUT_TYPE {
  COLOR = 'color',
  NUMBER = 'number',
  BOOLEAN = 'boolean',
  SELECT = 'select',
  TOGGLE = 'toggle',
}

export const ValueInputFactory = ({
  fieldPicker,
  commandPicker,
  scriptEditor,
  numberInput,
  toggleButton,
  getPath,
  type,
  object,
  hint,
  inputType = GENERATOR_INPUT_TYPE.INPUT,
  valueInputLabel = i18n.t('label.value'),
  isDisabled = false,
  prefix,
  children,
  placeholder,
  optionsFilter,
  options,
  onSaveField,
  formInput,
  clearColor,
  errorMessage,
  isShowFormatDateField,
}: Props) => {
  const {
    setValue,
    watch,
    formState: { errors },
  } = useFormContext()

  const { t } = useTranslation()

  const watchValue: ConfigField | string | AutocompleteOption = watch(
    getPath(getKeyByBindType(type))
  )

  const watchFormatDate = watch(getPath(`field.formatDate`))

  const [initialValue, setInitialValue] = useState<ConfigField>()
  const componentInstanceId = useId()
  const componentFieldKey = [type, inputType, componentInstanceId].join('-')

  const valueType = last((watchValue as ConfigField)?.pathArray)?.valueType || null

  // ModalRefPathPicker restore value on cancel
  useEffect(() => {
    if (!initialValue?.field) {
      const clonedValue = structuredClone(watchValue)
      setInitialValue(clonedValue as ConfigField)
    }

    return () => setInitialValue(undefined)
  }, [(watchValue as ConfigField)?.field])

  const handleChange = useCallback((key: GANTT_BIND_TYPE_LIST, value: string | undefined) => {
    setValue(getPath(key), value, { shouldDirty: true })
  }, [])

  const handleClearColorInput = useCallback(
    (handler: (value: string | undefined) => void) => {
      clearColor ? handleChange(GANTT_BIND_TYPE_LIST.STATIC, clearColor) : handler(undefined)
    },
    [handleChange, clearColor]
  )

  const handleOnSaveField = useCallback((value: ConfigField) => {
    setValue(getPath(`field.formatDate`), null)

    fieldPicker?.hasCommands &&
      setValue(getPath(`field.commands`), getCommandsFormattedRequest(value.commands || []))
    onSaveField?.(value)
  }, [])

  const handleChangeFormatDate = (value?: AutocompleteOption) => {
    setValue(getPath(`field.formatDate`), value?.id || null, { shouldDirty: true })
  }

  const filteredOptions = useMemo(() => {
    if (optionsFilter && options) {
      return optionsFilter(options, type)
    }

    return options || []
  }, [options, optionsFilter])

  switch (type) {
    case GANTT_BIND_TYPE_LIST.FIELD:
      return (
        <>
          <ModalRefPathPicker
            key={componentFieldKey}
            hasField
            commandsName={getPath(`field.commands`)}
            currentValue={initialValue}
            embeddedObjectPickerControllerName={getPath(`field.pathArray`)}
            existingPaths={fieldPicker?.existingPaths}
            fieldName={getPath(`field.field`)}
            hasCommands={fieldPicker?.hasCommands}
            isDisabled={isDisabled}
            label={valueInputLabel}
            name={getPath(`field.pathStr`)}
            object={object}
            optionsFilter={optionsFilter}
            pickerName={getPath('field')}
            prefix={prefix}
            selectedPaths={fieldPicker?.selectedPaths}
            onSave={handleOnSaveField}
          />
          {valueType === FIELD_VALUE_TYPE.DATETIME && isShowFormatDateField && (
            <FormInput
              autocompleteOptions={FORMATS_DATE_OPTIONS}
              inputType={GENERATOR_INPUT_TYPE.AUTOCOMPLETE}
              label={t('ganttCreate.columnForm.columnInputs.formatDate.label')}
              name={getPath(`field.formatDate`)}
              placeholder={t('ganttCreate.columnForm.columnInputs.formatDate.placeholder')}
              value={watchFormatDate ? { id: watchFormatDate, label: watchFormatDate } : null}
              onChangeAutocomplete={handleChangeFormatDate}
            />
          )}
        </>
      )

    case GANTT_BIND_TYPE_LIST.STATIC:
      switch (inputType) {
        case INPUT_TYPE.COLOR:
          return (
            <>
              <ColorSettings
                key={componentFieldKey}
                clearableColor
                color={watchValue as string}
                isEdit={!isDisabled}
                label={valueInputLabel ?? i18n.t('label.value')}
                labelMaxWidth={120}
                labelPlacement={'left'}
                onChangeColor={value => handleChange(GANTT_BIND_TYPE_LIST.STATIC, value)}
                onClearColor={handleClearColorInput}
              />
              {hint && <FormHelperText sx={{ pl: 15, ml: 0 }}>{hint}</FormHelperText>}
            </>
          )
        case INPUT_TYPE.NUMBER:
          return (
            <>
              <FormInput
                key={componentFieldKey}
                inputType={GENERATOR_INPUT_TYPE.INPUT}
                label={valueInputLabel}
                name={getPath(GANTT_BIND_TYPE_LIST.STATIC)}
                placeholder={placeholder}
                textFieldInputProps={{
                  inputComponent: ConstFormat as any,
                  inputProps: { decimalScale: 0, max: numberInput?.max },
                }}
              />
              {hint && <FormHelperText sx={{ pl: 15, ml: 0 }}>{hint}</FormHelperText>}
              {errorMessage && (
                <Typography color='error' sx={{ pl: 15, ml: 0 }}>
                  {errorMessage}
                </Typography>
              )}
            </>
          )
        case INPUT_TYPE.BOOLEAN:
          return (
            <>
              <FormInput
                formLabelSx={{ justifyContent: 'left' }}
                inputType={GENERATOR_INPUT_TYPE.CHECKBOX}
                label={valueInputLabel}
                name={getPath(GANTT_BIND_TYPE_LIST.STATIC)}
              />
              {hint && <FormHelperText sx={{ pl: 15, ml: 0 }}>{hint}</FormHelperText>}
            </>
          )
        case INPUT_TYPE.SELECT:
          return (
            <>
              <FormInput
                inputType={GENERATOR_INPUT_TYPE.SELECT}
                label={valueInputLabel}
                name={getPath(GANTT_BIND_TYPE_LIST.STATIC)}
                selectOptions={filteredOptions}
              />
              {hint && <FormHelperText sx={{ pl: 15, ml: 0 }}>{hint}</FormHelperText>}
            </>
          )
        case INPUT_TYPE.TOGGLE:
          return (
            <>
              <FormInput
                formLabelSx={{ justifyContent: 'left' }}
                inputType={GENERATOR_INPUT_TYPE.TOGGLE_BUTTON_GROUP}
                label={valueInputLabel}
                name={getPath(GANTT_BIND_TYPE_LIST.STATIC)}
                toggleButtonOptionsList={toggleButton?.toggleButtonOptions}
                toggleButtonSize={'small'}
              />
              {hint && <FormHelperText sx={{ pl: 15, ml: 0 }}>{hint}</FormHelperText>}
            </>
          )
        default:
          return (
            <>
              <FormInput
                key={componentFieldKey}
                inputType={inputType}
                label={valueInputLabel}
                name={getPath(GANTT_BIND_TYPE_LIST.STATIC)}
                placeholder={placeholder}
              />
              {hint && <FormHelperText sx={{ pl: 15, ml: 0 }}>{hint}</FormHelperText>}
            </>
          )
      }

    case GANTT_BIND_TYPE_LIST.JS:
      return (
        <ScriptValueEditor
          key={componentFieldKey}
          hasError={has(errors, getPath(GANTT_BIND_TYPE_LIST.JS))}
          hint={hint}
          label={valueInputLabel}
          language={'js'}
          maxInputLength={10000}
          noValidate={scriptEditor?.noValidate}
          path={getPath(type)}
          placeholder={placeholder}
          validator={scriptEditor?.validator}
          value={watchValue as string}
          onAfterValidationScript={scriptEditor?.onAfterValidation}
          onChange={value => handleChange(GANTT_BIND_TYPE_LIST.JS, value)}
        />
      )

    case GANTT_BIND_TYPE_LIST.JSX:
      return (
        <ScriptValueEditor
          key={componentFieldKey}
          hasError={has(errors, getPath(GANTT_BIND_TYPE_LIST.JSX))}
          label={valueInputLabel}
          language={'jsx'}
          path={getPath(type)}
          value={watchValue as string}
          onChange={value => handleChange(GANTT_BIND_TYPE_LIST.JSX, value)}
        />
      )

    case GANTT_BIND_TYPE_LIST.ICON:
      return (
        <IconSettings
          key={componentFieldKey}
          path={getPath(type)}
          value={watchValue as string}
          onChange={value => handleChange(GANTT_BIND_TYPE_LIST.ICON, value)}
        />
      )

    case GANTT_BIND_TYPE_LIST.FIELD_ARRAY:
      return (
        <PickerFieldArrayController
          key={componentFieldKey}
          disabled={isDisabled}
          existingPaths={fieldPicker?.existingPaths}
          isFlatOptions={false}
          isShowFormatDateField={isShowFormatDateField}
          label={valueInputLabel ?? i18next.t('label.value')}
          name={getPath(GANTT_BIND_TYPE_LIST.FIELD_ARRAY)}
          object={object}
          optionsFilter={optionsFilter}
          prefix={prefix}
        />
      )

    case GANTT_BIND_TYPE_LIST.JSON:
      return (
        <ScriptValueEditor
          key={componentFieldKey}
          hasError={has(errors, getPath(GANTT_BIND_TYPE_LIST.JSON))}
          hint={hint}
          label={valueInputLabel}
          language={'json'}
          maxInputLength={10000}
          path={getPath(type)}
          placeholder={placeholder}
          validator={scriptEditor?.validator}
          value={watchValue as string}
          onAfterValidationScript={scriptEditor?.onAfterValidation}
          onChange={value => handleChange(GANTT_BIND_TYPE_LIST.JSON, value)}
        />
      )

    case GANTT_BIND_TYPE_LIST.COMMANDS:
      return (
        <CommandPickerController
          key={componentFieldKey}
          isAnotherObjectWithRequestData
          availableRefObjectCodes={commandPicker?.availableRefObjectCodes}
          canChangeRefObject={commandPicker?.canChangeRefObject}
          commandName={'name'}
          isSingle={!commandPicker?.isMulti}
          isTypeHidden={commandPicker?.isTypeHidden}
          name={getPath(GANTT_BIND_TYPE_LIST.COMMANDS)}
          objectCode={object?.id || ''}
        />
      )

    case GANTT_BIND_TYPE_LIST.FORM:
    case GANTT_BIND_TYPE_LIST.TOOLTIP:
    case GANTT_BIND_TYPE_LIST.CONTEXT_MENU:
      return (
        <FormInput
          key={componentFieldKey}
          autocompleteOptions={filteredOptions as AutocompleteOption[]}
          getOptionDisabled={option => option.id === (watchValue as AutocompleteOption)?.id}
          inputType={GENERATOR_INPUT_TYPE.AUTOCOMPLETE}
          label={valueInputLabel}
          name={getPath(type)}
          placeholder={placeholder}
          rules={{ required: true }}
          renderAutocompleteOption={(
            props: React.HTMLAttributes<HTMLLIElement>,
            option: { id: string | number; label: string }
          ) => (
            <AutocompleteOptionsObject
              list={formInput?.selectedOptions || []}
              option={option}
              otherProps={props}
            />
          )}
        />
      )

    case GANTT_BIND_TYPE_LIST.CUSTOM:
      return <>{!!children && children}</>

    case GANTT_BIND_TYPE_LIST.COMMAND: // не задаётся импутами в конфигураторе но участвует
    default:
      return <></>
  }
}
